/**
 * SoundManager - A comprehensive audio management class for Three.js
 * Handles both sound effects and background ambience
 */

import * as THREE from 'three';

class SoundManager {
    constructor(camera, listener = null) {
        this.sounds = {};
        this.ambientSounds = {};
        this.masterVolume = 1.0;
        this.effectsVolume = 1.0;
        this.ambienceVolume = 0.5;

        // Set up audio listener if not provided
        if (!listener) {
            this.listener = new THREE.AudioListener();
            if (camera) camera.add(this.listener);
        } else {
            this.listener = listener;
        }

        // Create audio loader
        this.audioLoader = new THREE.AudioLoader();

        // Track loaded resources
        this.loadingManager = new THREE.LoadingManager();
        this.isLoading = false;
    }

    /**
     * Set the master volume for all sounds
     * @param {number} value - Volume level from 0.0 to 1.0
     */
    setMasterVolume(value) {
        this.masterVolume = Math.max(0, Math.min(1, value));

        // Update all sounds with new master volume
        Object.values(this.sounds).forEach(sound => {
            sound.setVolume(this.effectsVolume * this.masterVolume);
        });

        Object.values(this.ambientSounds).forEach(sound => {
            sound.setVolume(this.ambienceVolume * this.masterVolume);
        });
    }

    /**
     * Set the effects volume level
     * @param {number} value - Volume level from 0.0 to 1.0
     */
    setEffectsVolume(value) {
        this.effectsVolume = Math.max(0, Math.min(1, value));

        // Update all effect sounds
        Object.values(this.sounds).forEach(sound => {
            sound.setVolume(this.effectsVolume * this.masterVolume);
        });
    }

    /**
     * Set the ambience volume level
     * @param {number} value - Volume level from 0.0 to 1.0
     */
    setAmbienceVolume(value) {
        this.ambienceVolume = Math.max(0, Math.min(1, value));

        // Update all ambient sounds
        Object.values(this.ambientSounds).forEach(sound => {
            sound.setVolume(this.ambienceVolume * this.masterVolume);
        });
    }

    /**
     * Preload sound effect
     * @param {string} name - Unique identifier for this sound
     * @param {string} path - File path to the sound
     * @param {Function} callback - Optional callback when loading completes
     */
    loadSound(name, path, callback = null) {
        this.isLoading = true;

        this.audioLoader.load(
            path,
            (buffer) => {
                // Create a non-positional audio source
                const sound = new THREE.Audio(this.listener);
                sound.setBuffer(buffer);
                sound.setVolume(this.effectsVolume * this.masterVolume);
                this.sounds[name] = sound;

                if (callback) callback();
                this.isLoading = false;
            },
            // onProgress callback
            (xhr) => {
                //console.log(`${name} sound: ${(xhr.loaded / xhr.total * 100)}% loaded`);
            },
            // onError callback
            (err) => {
                //console.error(`Error loading sound ${name}:`, err);
                this.isLoading = false;
            }
        );
    }

    /**
     * Preload ambient sound
     * @param {string} name - Unique identifier for this ambient sound
     * @param {string} path - File path to the sound
     * @param {boolean} loop - Whether the sound should loop
     * @param {Function} callback - Optional callback when loading completes
     */
    loadAmbientSound(name, path, loop = true, callback = null) {
        this.isLoading = true;

        this.audioLoader.load(
            path,
            (buffer) => {
                // Create a non-positional audio source
                const sound = new THREE.Audio(this.listener);
                sound.setBuffer(buffer);
                sound.setVolume(this.ambienceVolume * this.masterVolume);
                sound.setLoop(loop);
                this.ambientSounds[name] = sound;

                if (callback) callback();
                this.isLoading = false;
            },
            // onProgress callback
            (xhr) => {
                //console.log(`${name} ambient sound: ${(xhr.loaded / xhr.total * 100)}% loaded`);
            },
            // onError callback
            (err) => {
                //console.error(`Error loading ambient sound ${name}:`, err);
                this.isLoading = false;
            }
        );
    }

    /**
     * Load a positional sound (3D sound effect)
     * @param {string} name - Unique identifier for this sound
     * @param {string} path - File path to the sound
     * @param {THREE.Object3D} object - The object to attach the sound to
     * @param {number} refDistance - Distance model reference distance 
     * @param {Function} callback - Optional callback when loading completes
     */
    loadPositionalSound(name, path, object, refDistance = 1, callback = null) {
        this.isLoading = true;

        this.audioLoader.load(
            path,
            (buffer) => {
                // Create a positional audio source
                const sound = new THREE.PositionalAudio(this.listener);
                sound.setBuffer(buffer);
                sound.setRefDistance(refDistance);
                sound.loop = true;
                sound.setVolume(this.effectsVolume * this.masterVolume);

                // Attach the sound to the object
                object.add(sound);

                // Store reference
                this.sounds[name] = sound;

                if (callback) callback();
                this.isLoading = false;
            },
            // onProgress callback
            (xhr) => {
                //console.log(`${name} positional sound: ${(xhr.loaded / xhr.total * 100)}% loaded`);
            },
            // onError callback
            (err) => {
                //console.error(`Error loading positional sound ${name}:`, err);
                this.isLoading = false;
            }
        );
    }

    /**
     * Play a sound effect
     * @param {string} name - Name of the sound to play
     * @param {boolean} interrupt - Whether to restart if already playing
     * @return {boolean} - Whether the sound was successfully played
     */
    play(name, interrupt = true) {
        const sound = this.sounds[name];
        if (!sound) {
            //console.warn(`Sound "${name}" not found`);
            return false;
        }

        if (sound.isPlaying && !interrupt) {
            return false;
        }

        if (sound.isPlaying) {
            sound.stop();
        }

        sound.play();
        return true;
    }

    /**
     * Play an ambient sound
     * @param {string} name - Name of the ambient sound to play
     * @param {boolean} fadeIn - Whether to fade in the sound
     * @param {number} fadeTime - Time in seconds for fade in
     */
    playAmbience(name, fadeIn = true, fadeTime = 2.0) {
        const sound = this.ambientSounds[name];
        if (!sound) {
            //console.warn(`Ambient sound "${name}" not found`);
            return false;
        }

        if (sound.isPlaying) {
            return false;
        }

        if (fadeIn) {
            // Store the target volume and start at 0
            const targetVolume = sound.getVolume();
            sound.setVolume(0);
            sound.play();

            // Fade in
            const startTime = performance.now();
            const updateVolume = () => {
                const elapsedTime = (performance.now() - startTime) / 1000;
                const progress = Math.min(elapsedTime / fadeTime, 1);
                sound.setVolume(targetVolume * progress);

                if (progress < 1) {
                    requestAnimationFrame(updateVolume);
                }
            };

            requestAnimationFrame(updateVolume);
        } else {
            sound.play();
        }

        return true;
    }

    /**
     * Stop a specific sound
     * @param {string} name - Name of the sound to stop
     * @param {boolean} fadeOut - Whether to fade out before stopping
     * @param {number} fadeTime - Time in seconds for fade out
     */
    stop(name, fadeOut = false, fadeTime = 1.0) {
        const sound = this.sounds[name] || this.ambientSounds[name];
        if (!sound) {
            //console.warn(`Sound "${name}" not found`);
            return;
        }

        if (!sound.isPlaying) {
            return;
        }

        if (fadeOut) {
            // Store initial volume
            const initialVolume = sound.getVolume();
            const startTime = performance.now();

            // Fade out
            const updateVolume = () => {
                const elapsedTime = (performance.now() - startTime) / 1000;
                const progress = Math.min(elapsedTime / fadeTime, 1);
                sound.setVolume(initialVolume * (1 - progress));

                if (progress < 1) {
                    requestAnimationFrame(updateVolume);
                } else {
                    sound.stop();
                    // Restore original volume
                    sound.setVolume(initialVolume);
                }
            };

            requestAnimationFrame(updateVolume);
        } else {
            sound.stop();
        }
    }

    /**
     * Stop all sounds including ambience
     * @param {boolean} fadeOut - Whether to fade out
     * @param {number} fadeTime - Time in seconds for fade out
     */
    stopAll(fadeOut = false, fadeTime = 1.0) {
        // Stop regular sounds
        Object.keys(this.sounds).forEach(name => {
            this.stop(name, fadeOut, fadeTime);
        });

        // Stop ambient sounds
        Object.keys(this.ambientSounds).forEach(name => {
            this.stop(name, fadeOut, fadeTime);
        });
    }

    /**
     * Pause all currently playing sounds
     */
    pauseAll() {
        this.listener.context.suspend();
    }

    /**
     * Resume all paused sounds
     */
    resumeAll() {
        this.listener.context.resume();
    }

    /**
     * Crossfade between two ambient sounds
     * @param {string} from - Name of the ambient sound to fade out
     * @param {string} to - Name of the ambient sound to fade in
     * @param {number} duration - Duration of crossfade in seconds
     */
    crossfadeAmbience(from, to, duration = 3.0) {
        const fromSound = this.ambientSounds[from];
        const toSound = this.ambientSounds[to];

        if (!fromSound || !toSound) {
            //console.warn(`One or both ambient sounds not found for crossfade`);
            return;
        }

        // Store original volumes
        const fromVolume = fromSound.getVolume();
        const toVolume = toSound.getVolume();

        // Start playing the target sound at zero volume
        toSound.setVolume(0);
        if (!toSound.isPlaying) {
            toSound.play();
        }

        // Perform crossfade
        const startTime = performance.now();
        const updateVolumes = () => {
            const elapsedTime = (performance.now() - startTime) / 1000;
            const progress = Math.min(elapsedTime / duration, 1);

            // Fade out source sound
            fromSound.setVolume(fromVolume * (1 - progress));

            // Fade in target sound
            toSound.setVolume(toVolume * progress);

            if (progress < 1) {
                requestAnimationFrame(updateVolumes);
            } else {
                fromSound.stop();
                fromSound.setVolume(fromVolume); // Restore original volume
            }
        };

        requestAnimationFrame(updateVolumes);
    }

    /**
     * Update method to call in animation loop for any ongoing processes
     * @param {number} delta - Time delta between frames
     */
    update(delta) {
        // Can be extended for time-based effects or analyzers
    }
}

// Export the class
export default SoundManager;